import { expectArray, expectBoolean, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseFamilyScheduleDisplayFlags } from "./family";
import { parseTenantServiceCategory, parseTenantUiLabelProfile } from "./tenant-metadata";
export const PUBLIC_EVENT_STATUSES = ["draft", "published", "hidden"];
export const PUBLIC_ATTACHMENT_TYPES = ["image", "pdf", "video"];
export const PUBLIC_ATTACHMENT_UPLOAD_STATUSES = ["draft", "uploadIssued", "uploaded", "failed"];
export const PUBLIC_ATTACHMENT_PUBLISH_STATUSES = ["draft", "published", "hidden"];
function parsePublicEventStatus(value, path) {
    const parsed = expectString(value, path);
    if (!PUBLIC_EVENT_STATUSES.includes(parsed)) {
        throw new Error(`${path} must be one of: ${PUBLIC_EVENT_STATUSES.join(", ")}`);
    }
    return parsed;
}
function parsePublicAttachmentType(value, path) {
    const parsed = expectString(value, path);
    if (!PUBLIC_ATTACHMENT_TYPES.includes(parsed)) {
        throw new Error(`${path} must be one of: ${PUBLIC_ATTACHMENT_TYPES.join(", ")}`);
    }
    return parsed;
}
function parsePublicAttachmentUploadStatus(value, path) {
    const parsed = expectString(value, path);
    if (!PUBLIC_ATTACHMENT_UPLOAD_STATUSES.includes(parsed)) {
        throw new Error(`${path} must be one of: ${PUBLIC_ATTACHMENT_UPLOAD_STATUSES.join(", ")}`);
    }
    return parsed;
}
function parsePublicAttachmentPublishStatus(value, path) {
    const parsed = expectString(value, path);
    if (!PUBLIC_ATTACHMENT_PUBLISH_STATUSES.includes(parsed)) {
        throw new Error(`${path} must be one of: ${PUBLIC_ATTACHMENT_PUBLISH_STATUSES.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function parsePublicTopSettings(value, path = "publicSettings") {
    const record = expectRecord(value, path);
    return {
        showPublicSchedule: expectBoolean(record.showPublicSchedule, `${path}.showPublicSchedule`),
        showPublicEvents: expectBoolean(record.showPublicEvents, `${path}.showPublicEvents`)
    };
}
export function parseAdminPublicSettings(value, path = "adminPublicSettings") {
    const record = expectRecord(value, path);
    return {
        showPublicSchedule: expectBoolean(record.showPublicSchedule, `${path}.showPublicSchedule`),
        showPublicEvents: expectBoolean(record.showPublicEvents, `${path}.showPublicEvents`),
        publicScheduleWindowDays: expectNumber(record.publicScheduleWindowDays, `${path}.publicScheduleWindowDays`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        updatedAt: expectOptionalString(record.updatedAt, `${path}.updatedAt`),
        updatedByPlatformUserId: expectOptionalString(record.updatedByPlatformUserId, `${path}.updatedByPlatformUserId`)
    };
}
export function parseUpdateAdminPublicSettingsRequest(value, path = "updateAdminPublicSettingsRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "showPublicSchedule")) {
        parsed.showPublicSchedule = expectBoolean(record.showPublicSchedule, `${path}.showPublicSchedule`);
    }
    if (hasOwn(record, "showPublicEvents")) {
        parsed.showPublicEvents = expectBoolean(record.showPublicEvents, `${path}.showPublicEvents`);
    }
    if (hasOwn(record, "publicScheduleWindowDays")) {
        parsed.publicScheduleWindowDays = expectNumber(record.publicScheduleWindowDays, `${path}.publicScheduleWindowDays`);
    }
    return parsed;
}
export function parsePublicTenant(value, path = "publicTenant") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        brandName: expectOptionalString(record.brandName, `${path}.brandName`),
        displayName: expectOptionalString(record.displayName, `${path}.displayName`),
        serviceCategory: parseTenantServiceCategory(record.serviceCategory, `${path}.serviceCategory`),
        uiLabelProfile: parseTenantUiLabelProfile(record.uiLabelProfile, `${path}.uiLabelProfile`),
        isDemoTenant: expectBoolean(record.isDemoTenant, `${path}.isDemoTenant`),
        publicSettings: parsePublicTopSettings(record.publicSettings, `${path}.publicSettings`)
    };
}
export function parsePublicScheduleItem(value, path = "publicScheduleItem") {
    const record = expectRecord(value, path);
    return {
        occurrenceId: expectString(record.occurrenceId, `${path}.occurrenceId`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        occurrenceDate: expectString(record.occurrenceDate, `${path}.occurrenceDate`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        studioName: expectOptionalString(record.studioName, `${path}.studioName`),
        studioDecisionStatus: expectOptionalString(record.studioDecisionStatus, `${path}.studioDecisionStatus`),
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        lifecycleStatus: expectString(record.lifecycleStatus, `${path}.lifecycleStatus`),
        resolutionType: expectString(record.resolutionType, `${path}.resolutionType`),
        displayFlags: parseFamilyScheduleDisplayFlags(record.displayFlags, `${path}.displayFlags`)
    };
}
export function parsePublicScheduleMonthGroup(value, path = "publicScheduleMonthGroup") {
    const record = expectRecord(value, path);
    return {
        yearMonth: expectString(record.yearMonth, `${path}.yearMonth`),
        label: expectString(record.label, `${path}.label`),
        items: expectArray(record.items, parsePublicScheduleItem, `${path}.items`)
    };
}
export function parsePublicScheduleResponse(value, path = "publicScheduleResponse") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        todayDate: expectString(record.todayDate, `${path}.todayDate`),
        items: expectArray(record.items, parsePublicScheduleItem, `${path}.items`),
        monthGroups: expectArray(record.monthGroups, parsePublicScheduleMonthGroup, `${path}.monthGroups`)
    };
}
export function parsePublicEventAttachment(value, path = "publicEventAttachment") {
    const record = expectRecord(value, path);
    return {
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        attachmentType: parsePublicAttachmentType(record.attachmentType, `${path}.attachmentType`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        objectPath: expectOptionalString(record.objectPath, `${path}.objectPath`),
        mimeType: expectOptionalString(record.mimeType, `${path}.mimeType`),
        fileSizeBytes: expectNullable(record.fileSizeBytes, expectNumber, `${path}.fileSizeBytes`),
        uploadStatus: parsePublicAttachmentUploadStatus(record.uploadStatus, `${path}.uploadStatus`),
        publishStatus: parsePublicAttachmentPublishStatus(record.publishStatus, `${path}.publishStatus`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`),
        createdAt: expectOptionalString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectOptionalString(record.updatedAt, `${path}.updatedAt`),
        uploadedByPlatformUserId: expectOptionalString(record.uploadedByPlatformUserId, `${path}.uploadedByPlatformUserId`),
        downloadUrl: expectOptionalString(record.downloadUrl, `${path}.downloadUrl`),
        expiresAt: expectOptionalString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parsePublicEventSummary(value, path = "publicEventSummary") {
    const record = expectRecord(value, path);
    return {
        eventId: expectString(record.eventId, `${path}.eventId`),
        title: expectString(record.title, `${path}.title`),
        summary: expectOptionalString(record.summary, `${path}.summary`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        attachments: expectArray(record.attachments, parsePublicEventAttachment, `${path}.attachments`)
    };
}
export function parsePublicEventsResponse(value, path = "publicEventsResponse") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        items: expectArray(record.items, parsePublicEventSummary, `${path}.items`)
    };
}
export function parsePublicEventDetail(value, path = "publicEventDetail") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        eventId: expectString(record.eventId, `${path}.eventId`),
        title: expectString(record.title, `${path}.title`),
        summary: expectOptionalString(record.summary, `${path}.summary`),
        body: expectOptionalString(record.body, `${path}.body`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        attachments: expectArray(record.attachments, parsePublicEventAttachment, `${path}.attachments`)
    };
}
export function parseAdminPublicEvent(value, path = "adminPublicEvent") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        eventId: expectString(record.eventId, `${path}.eventId`),
        title: expectString(record.title, `${path}.title`),
        summary: expectOptionalString(record.summary, `${path}.summary`),
        body: expectOptionalString(record.body, `${path}.body`),
        publicStatus: parsePublicEventStatus(record.publicStatus, `${path}.publicStatus`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`),
        attachments: expectArray(record.attachments, parsePublicEventAttachment, `${path}.attachments`),
        createdAt: expectOptionalString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectOptionalString(record.updatedAt, `${path}.updatedAt`),
        createdByPlatformUserId: expectOptionalString(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        updatedByPlatformUserId: expectOptionalString(record.updatedByPlatformUserId, `${path}.updatedByPlatformUserId`)
    };
}
export function parseAdminPublicEventsResponse(value, path = "adminPublicEventsResponse") {
    const record = expectRecord(value, path);
    return {
        tenantSlug: expectString(record.tenantSlug, `${path}.tenantSlug`),
        timeZone: expectString(record.timeZone, `${path}.timeZone`),
        items: expectArray(record.items, parseAdminPublicEvent, `${path}.items`)
    };
}
export function parseCreateAdminPublicEventRequest(value, path = "createAdminPublicEventRequest") {
    const record = expectRecord(value, path);
    return {
        title: expectString(record.title, `${path}.title`),
        summary: expectOptionalString(record.summary, `${path}.summary`),
        body: expectOptionalString(record.body, `${path}.body`),
        publicStatus: parsePublicEventStatus(record.publicStatus, `${path}.publicStatus`),
        publishFrom: expectOptionalString(record.publishFrom, `${path}.publishFrom`),
        publishTo: expectOptionalString(record.publishTo, `${path}.publishTo`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`),
        attachments: expectArray(record.attachments, parsePublicEventAttachment, `${path}.attachments`)
    };
}
export function parseUpdateAdminPublicEventRequest(value, path = "updateAdminPublicEventRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "title")) {
        parsed.title = expectString(record.title, `${path}.title`);
    }
    if (hasOwn(record, "summary")) {
        parsed.summary = expectOptionalString(record.summary, `${path}.summary`);
    }
    if (hasOwn(record, "body")) {
        parsed.body = expectOptionalString(record.body, `${path}.body`);
    }
    if (hasOwn(record, "publicStatus")) {
        parsed.publicStatus = parsePublicEventStatus(record.publicStatus, `${path}.publicStatus`);
    }
    if (hasOwn(record, "publishFrom")) {
        parsed.publishFrom = expectOptionalString(record.publishFrom, `${path}.publishFrom`);
    }
    if (hasOwn(record, "publishTo")) {
        parsed.publishTo = expectOptionalString(record.publishTo, `${path}.publishTo`);
    }
    if (hasOwn(record, "sortOrder")) {
        parsed.sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    }
    if (hasOwn(record, "attachments")) {
        parsed.attachments = expectArray(record.attachments, parsePublicEventAttachment, `${path}.attachments`);
    }
    return parsed;
}
export function parseCreateAdminPublicEventAttachmentRequest(value, path = "createAdminPublicEventAttachmentRequest") {
    const record = expectRecord(value, path);
    const parsed = {
        attachmentType: parsePublicAttachmentType(record.attachmentType, `${path}.attachmentType`)
    };
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectOptionalString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "mimeType")) {
        parsed.mimeType = expectOptionalString(record.mimeType, `${path}.mimeType`);
    }
    if (hasOwn(record, "sortOrder")) {
        parsed.sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    }
    return parsed;
}
export function parseUpdateAdminPublicEventAttachmentRequest(value, path = "updateAdminPublicEventAttachmentRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectOptionalString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "sortOrder")) {
        parsed.sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    }
    return parsed;
}
export function parsePublicEventAttachmentUploadSessionResponse(value, path = "publicEventAttachmentUploadSessionResponse") {
    const record = expectRecord(value, path);
    return {
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        uploadUrl: expectString(record.uploadUrl, `${path}.uploadUrl`),
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parseCompletePublicEventAttachmentUploadRequest(value, path = "completePublicEventAttachmentUploadRequest") {
    const record = expectRecord(value, path);
    return {
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`),
        originalFileName: expectOptionalString(record.originalFileName, `${path}.originalFileName`),
        mimeType: expectOptionalString(record.mimeType, `${path}.mimeType`),
        fileSizeBytes: expectNullable(record.fileSizeBytes, expectNumber, `${path}.fileSizeBytes`)
    };
}
